import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatCard } from "@/components/StatCard";
import { TransactionItem } from "@/components/TransactionItem";
import { ProductCard } from "@/components/ProductCard";
import { Logo } from "@/components/Logo";
import {
  StatCardSkeleton,
  TransactionItemSkeleton,
  ProductCardSkeleton,
} from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { useData } from "@/contexts/DataContext";
import {
  TrendingUp,
  Calendar,
  CalendarDays,
  CalendarRange,
  Plus,
  ArrowRight,
  Receipt,
  IndianRupee,
  Heart,
  Search,
  Zap,
  ChevronDown,
  ChevronUp,
  Bell,
  Eye,
  EyeOff,
  Star,
} from "lucide-react";
import { Link } from "wouter";
import { useState, useMemo, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { BellRing, X, Smartphone, MoreVertical, ExternalLink } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { RecordSuccessPopup } from "@/components/RecordSuccessPopup";
import { BillingBar } from "@/components/BillingBar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Dashboard() {
  const { toast } = useToast();
  const {
    dashboardStats,
    chartData,
    transactions,
    products,
    isLoading,
    deleteTransaction,
    editProduct,
    createTransactions,
    userMode,
    businessProfile,
    businessVerified,
    readNotificationIds,
    dismissedNotificationIds,
    hideFavoriteProducts,
    setHideFavoriteProducts,
    hideSalesSummary,
    setHideSalesSummary,
  } = useData();

  const [selectedItems, setSelectedItems] = useState<Map<string, any>>(
    new Map(),
  );
  const [lastTappedProductId, setLastTappedProductId] = useState<string | null>(
    null,
  );
  const [billDiscount, setBillDiscount] = useState<number>(0);
  const [isGstBill, setIsGstBill] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [profitPeriod, setProfitPeriod] = useState<
    "weekly" | "monthly" | "yearly"
  >("weekly");
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showProDiscountDialog, setShowProDiscountDialog] = useState(false);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [isNotifDenied, setIsNotifDenied] = useState(false);

  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      const dismissed = localStorage.getItem("notif_banner_dismissed");
      if (!dismissed) setShowNotifBanner(true);
    }

    const checkDeniedStatus = async () => {
      const dismissedDenied = localStorage.getItem("denied_notif_card_dismissed");
      if (dismissedDenied) return;

      if (typeof Notification !== "undefined" && Notification.permission === 'denied') {
        setIsNotifDenied(true);
        return;
      }

      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const status = await PushNotifications.checkPermissions();
          if (status.receive === 'denied' || status.receive === 'prompt-with-rationale') {
            setIsNotifDenied(true);
          }
        }
      } catch (e) {
        console.error("Failed to check push permissions", e);
      }
    };

    checkDeniedStatus();
  }, []);

  useEffect(() => {
    // Fire this natively so the Android FIAM SDK hears it
    if (Capacitor.isNativePlatform()) {
      FirebaseAnalytics.logEvent({ name: 'dashboard_ready', params: {} })
        .catch(e => console.error("Native Analytics error:", e));
    }
  }, []);

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    try {
      const { getTokenForNotifications, auth, db } = await import(
        "@/lib/firebase"
      );
      const { doc, setDoc, serverTimestamp } = await import(
        "firebase/firestore"
      );
      const token = await getTokenForNotifications();
      if (token) {
        const uid = auth.currentUser?.uid;
        if (uid) {
          await setDoc(
            doc(db, "users", uid, "fcm_tokens", token),
            { token, updatedAt: serverTimestamp(), platform: "web" },
            { merge: true },
          );
        }
        setShowNotifBanner(false);
        setIsNotifDenied(false);
        toast({ title: "Notifications enabled!" });
      } else {
        localStorage.setItem("notif_banner_dismissed", "true");
        setShowNotifBanner(false);
      }
    } catch {
      setShowNotifBanner(false);
    } finally {
      setNotifLoading(false);
    }
  };

  const [lastInvoiceData, setLastInvoiceData] = useState<{
    summary: string;
    total: number;
    items: any[];
    discount: number;
    orderId?: string;
    fullOrderId?: string;
    isGstBill?: boolean;
  } | null>(null);

  const generateInvoice = (
    saleItems: any[],
    total: number,
    discount: number = 0,
  ) => {
    const estimatedHeight =
      45 + 20 + 10 + saleItems.length * 8 + 30 + 25 + (businessVerified ? 5 : 0);
    const doc = new jsPDF({ unit: "mm", format: [80, estimatedHeight] });
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    const bName = (
      businessProfile?.businessName || "RETAIL STORE"
    ).toUpperCase();
    doc.text(bName, pageWidth / 2, yPos, { align: "center" });
    yPos += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    if (businessProfile?.businessAddress) {
      const splitAddress = doc.splitTextToSize(
        businessProfile.businessAddress,
        pageWidth - 20,
      );
      doc.text(splitAddress, pageWidth / 2, yPos, { align: "center" });
      yPos += splitAddress.length * 4;
    }
    if (businessProfile?.businessPhone) {
      doc.text(`Tel: ${businessProfile.businessPhone}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }
    if (businessProfile?.businessEmail) {
      doc.text(`Email: ${businessProfile.businessEmail}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }
    if (businessProfile?.businessWebsite) {
      doc.text(`WEB: ${businessProfile.businessWebsite}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }
    yPos += 2;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;
    doc.setFontSize(7);
    const dateStr = saleItems[0]?.createdAt
      ? new Date(saleItems[0].createdAt).toLocaleDateString()
      : new Date().toLocaleDateString();
    const timeStr = saleItems[0]?.createdAt
      ? new Date(saleItems[0].createdAt).toLocaleTimeString()
      : new Date().toLocaleTimeString();
    doc.text(`DATE: ${dateStr}`, 5, yPos);
    doc.text(`TIME: ${timeStr}`, pageWidth - 5, yPos, { align: "right" });
    yPos += 4;
    doc.text(`INV #: INV-${Date.now().toString().slice(-6)}`, 5, yPos);
    yPos += 6;
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;
    doc.setFont("helvetica", "bold");
    doc.text("Qty Item", 5, yPos);
    doc.text("Unit Price", 50, yPos, { align: "right" });
    doc.text("Total Price", pageWidth - 5, yPos, { align: "right" });
    yPos += 4;
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;
    doc.setFont("helvetica", "normal");
    const filteredItems = saleItems.filter(
      (item) =>
        item.productName !== "Discount" &&
        item.productName !== "Bill Discount" &&
        item.productId !== "bill-discount",
    );
    filteredItems.forEach((item) => {
      const name = item.productName.toUpperCase();
      const qty = Number(item.quantity);
      const rate = Number(item.rate);
      const totalVal = qty * rate;
      const splitName = doc.splitTextToSize(name, pageWidth - 40);
      doc.text(`${qty} ${splitName[0]}`, 5, yPos);
      let nameY = yPos;
      if (splitName.length > 1) {
        for (let i = 1; i < splitName.length; i++) {
          nameY += 4;
          doc.text(splitName[i], 10, nameY);
        }
      }
      doc.text(rate.toFixed(2), 50, yPos, { align: "right" });
      doc.text(totalVal.toFixed(2), pageWidth - 5, yPos, { align: "right" });
      yPos = Math.max(yPos + 5, nameY + 5);
    });
    yPos += 2;
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 8;
    const discountTotalValue = discount;
    const finalInvoiceTotal = total - discountTotalValue;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (discountTotalValue > 0) {
      doc.text("TOTAL BEFORE SAVINGS", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 6;
      doc.text("YOUR SAVINGS TODAY", 5, yPos);
      doc.text(`Rs. ${discountTotalValue.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    } else {
      doc.text("TOTAL", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    }
    doc.setFontSize(11);
    doc.text("NET PAYABLE", 5, yPos);
    doc.text(`Rs. ${finalInvoiceTotal.toFixed(2)}`, pageWidth - 5, yPos, {
      align: "right",
    });
    yPos += 10;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("THANK YOU FOR SHOPPING!", pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 5;
    doc.text("Visit again soon", pageWidth / 2, yPos, { align: "center" });
    yPos += 8;
    if (businessVerified) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      // keep in sync with the DAILYHISAAB VERIFIED badge
      doc.text("**** DAILYHISAAB VERIFIED ****", pageWidth / 2, yPos, {
        align: "center",
      });
      doc.setFont("helvetica", "normal");
      yPos += 5;
    }
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 255);
    doc.textWithLink("Generated by DailyHisaab", pageWidth / 2, yPos, {
      url: "https://dailyhisaab.com",
      align: "center",
    });
    return doc;
  };

  const shareOnWhatsApp = (
    total: number,
    saleItems: any[],
    discount: number = 0,
    orderId?: string,
  ) => {
    const bName = businessProfile?.businessName || "Retail Store";
    const itemsList = saleItems
      .map(
        (item) =>
          `• ${item.productName}: ${item.quantity} x ₹${item.rate} = ₹${(item.quantity * item.rate).toFixed(2)}`,
      )
      .join("\n");
    // keep in sync with the DAILYHISAAB VERIFIED badge — footer line, just before Generated-by
    const verifiedLine = businessVerified ? `\n**** DAILYHISAAB VERIFIED ****` : "";
    const businessInfo = `*${bName.toUpperCase()}*`;
    const finalTotal = total - discount;
    const discountText =
      discount > 0 ? `\nSavings: ₹${discount.toFixed(2)}` : "";
    const invoiceLink = orderId
      ? `\n\nView/Download Invoice: ${window.location.origin}/invoice/${orderId}`
      : "";
    const message = `${businessInfo}\n\n*INVOICE*\nDate: ${new Date().toLocaleDateString()}\n--------------------------\n${itemsList}\n--------------------------${discountText}\n*NET PAYABLE: ₹${finalTotal.toFixed(2)}*${invoiceLink}\n\nThank you for shopping!${verifiedLine}\n_Generated by DailyHisaab_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const recentTransactions = transactions.slice(0, 5);
  const favoriteProducts = products.filter((p) => p.isFavorite);
  const displayedFavorites =
    userMode === "pro" ? favoriteProducts : favoriteProducts.slice(0, 10);

  const searchedProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        (p.category && p.category.toLowerCase().includes(query)),
    );
  }, [products, searchQuery]);

  const handleQuantityChange = (
    productId: string,
    productName: string,
    rate: number,
    quantity: number,
  ) => {
    const newSelected = new Map(selectedItems);
    if (quantity === 0) {
      newSelected.delete(productId);
    } else {
      newSelected.set(productId, { productId, productName, rate, quantity });
    }
    setSelectedItems(newSelected);
  };

  const handleProductSelect = (product: any) => {
    setLastTappedProductId(product.id);
    const current = selectedItems.get(product.id);
    if (current) {
      handleQuantityChange(
        product.id,
        product.name,
        product.salePrice,
        current.quantity + 1,
      );
    } else {
      handleQuantityChange(product.id, product.name, product.salePrice, 1);
    }
  };

  const handleToggleFavorite = async (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      try {
        await editProduct({ ...product, isFavorite: !product.isFavorite });
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Could not update favorites: " + error.message,
          variant: "destructive"
        });
      }
    }
  };

  const handleSell = async (shouldShare = false, bypassDiscount = false) => {
    const items = Array.from(selectedItems.values());
    const total = items.reduce(
      (sum, item) => sum + item.quantity * item.rate,
      0,
    );

    if (userMode === "pro" && !bypassDiscount && billDiscount === 0) {
      setShowProDiscountDialog(true);
      return;
    }

    setIsProcessing(true);

    try {
      const itemsToRecord = [...items];
      if (billDiscount > 0) {
        itemsToRecord.push({
          productId: "bill-discount",
          productName: "Bill Discount",
          quantity: 1,
          rate: -billDiscount,
          total: -billDiscount,
        });
      }

      const recorded = await createTransactions(
        itemsToRecord,
        "sale",
        true,
        isGstBill,
      );

      const fullOrderId = recorded[0]?.fullOrderId;
      const orderId =
        fullOrderId || recorded[0]?.id.split("_").slice(0, 3).join("_");
      const summary = items
        .map((i) => `${i.quantity}x ${i.productName}`)
        .join(", ");

      setLastInvoiceData({
        summary,
        total: total,
        items: recorded.filter(
          (r: any) => !r.productName?.toLowerCase().includes("discount"),
        ),
        discount: billDiscount,
        orderId,
        fullOrderId,
        isGstBill: isGstBill,
      });

      setShowProDiscountDialog(false);
      setShowSharePopup(true);
      setSelectedItems(new Map());
      setBillDiscount(0);
      setIsGstBill(false);

      toast({ title: "Sale recorded successfully!" });
    } catch (error: any) {
      toast({
        title: "Error Recording Sale",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 100000) return `₹${(absAmount / 100000).toFixed(1)}L`;
    else if (absAmount >= 1000) return `₹${(absAmount / 1000).toFixed(1)}K`;
    return `₹${absAmount.toFixed(0)}`;
  };

  const hasUnreadAlerts = useMemo(() => {
    if (userMode !== "pro") return false;

    // If notifications are denied, ALWAYS show the red dot!
    if (isNotifDenied) return true;

    const readIds = new Set<string>(readNotificationIds);
    const dismissedIds = new Set<string>(dismissedNotificationIds);

    return products.some((p) => {
      if (p.stock === undefined) return false;
      if (p.stock === 0) {
        const id = `oos-${p.id}-${p.stock}`;
        return !readIds.has(id) && !dismissedIds.has(id);
      }
      if (
        p.lowStockAlert &&
        p.lowStockAlert > 0 &&
        p.stock <= p.lowStockAlert
      ) {
        const id = `low-${p.id}-${p.stock}`;
        return !readIds.has(id) && !dismissedIds.has(id);
      }
      return false;
    });
  }, [products, userMode, readNotificationIds, dismissedNotificationIds, isNotifDenied]);

  const profitByCategory = useMemo(() => {
    const now = new Date();
    const map = new Map<string, number>();
    transactions.forEach((t) => {
      if (t.type !== "sale") return;
      if ((t as any).cancelled) return;
      const tDate = t.createdAt ? new Date(t.createdAt) : null;
      if (!tDate) return;
      if (profitPeriod === "weekly") {
        const daysDiff =
          (now.getTime() - tDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff > 7) return;
      } else if (profitPeriod === "monthly") {
        if (
          tDate.getMonth() !== now.getMonth() ||
          tDate.getFullYear() !== now.getFullYear()
        )
          return;
      } else if (profitPeriod === "yearly") {
        if (tDate.getFullYear() !== now.getFullYear()) return;
      }
      const product = products.find((p) => p.id === t.productId);
      const cat = product?.category || "Other";
      const profit =
        ((product?.salePrice || 0) - (product?.costPrice || 0)) *
        (t.quantity || 1);
      map.set(cat, (map.get(cat) || 0) + profit);
    });
    const entries = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, v]) => s + Math.max(v, 0), 0);
    return { entries, total };
  }, [transactions, products, profitPeriod]);

  const DONUT_COLORS = ["#8B1A1A", "#E53E3E", "#F6AD55", "#CBD5E0", "#FC8181"];

  const donutData = useMemo(() => {
    const { entries, total } = profitByCategory;
    if (entries.length === 0 || total <= 0) return [];
    return entries.slice(0, 4).map(([name, value]) => ({
      name,
      value,
      pct: Math.round((value / total) * 100),
    }));
  }, [profitByCategory]);

  const topCategory = donutData[0] || null;
  const totalProfit = profitByCategory.total;

  return (
    <div className="bg-[#FDFCF7] dark:bg-[#1A1614] min-h-screen">
      {/* ── HEADER ── */}
      <div
        className={`z-30 bg-[#FDFCF7]/90 dark:bg-[#1A1614]/90 backdrop-blur-md ${userMode === "pro" ? "sticky top-0 border-b border-slate-100 dark:border-slate-800" : ""}`}
      >
        <div className="px-4 pt-3 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-2">
            {userMode === "pro" && (
              <Link href="/alerts">
                <button
                  className="relative w-10 h-10 rounded-full bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center shadow-sm"
                  data-testid="button-alerts-dashboard"
                >
                  <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  {hasUnreadAlerts && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 border border-white dark:border-slate-800" />
                  )}
                </button>
              </Link>
            )}
            <button
              className={`w-10 h-10 rounded-full border flex items-center justify-center shadow-sm transition-colors ${showSearch ? "text-white border-transparent" : "bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
              style={
                showSearch
                  ? {
                    background:
                      "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                  }
                  : {}
              }
              onClick={() => {
                setShowSearch(!showSearch);
                if (showSearch) setSearchQuery("");
              }}
              data-testid="button-toggle-search-dashboard"
            >
              <Search className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* ── SEARCH BAR ── */}
      {showSearch && (
        <div className="px-4 pb-4 pt-2">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search inventory to add to bill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-sm font-medium"
              autoFocus
              data-testid="input-dashboard-search"
            />
          </div>
          {searchedProducts.length > 0 && (
            <div className="mt-3 space-y-3">
              {searchedProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  selectedQuantity={
                    selectedItems.get(product.id)?.quantity || 0
                  }
                  onQuantityChange={(qty) =>
                    handleQuantityChange(
                      product.id,
                      product.name,
                      product.salePrice || 0,
                      qty,
                    )
                  }
                  onSelect={() => handleProductSelect(product)}
                  onToggleFavorite={handleToggleFavorite}
                  showFavoriteIcon={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SALES SUMMARY (UNIFIED WIDGET) ── */}
      <div className="px-4 pb-2 pt-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
              Sales Summary
            </h2>
            <button
              onClick={() => setHideSalesSummary(!hideSalesSummary)}
              className="text-slate-400 dark:text-slate-500"
              data-testid="button-toggle-stats-dashboard"
            >
              {!hideSalesSummary ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          <span
            className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border"
            style={{
              color: "#8B1A1A",
              borderColor: "rgba(139,26,26,0.2)",
              backgroundColor: "rgba(139,26,26,0.05)",
            }}
          >
            Live Stats
          </span>
        </div>

        <AnimatePresence>
          {!hideSalesSummary && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden mb-2">
                {isLoading ? (
                  <div className="grid grid-cols-2 divide-x divide-y divide-slate-100 dark:divide-slate-800">
                    <StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 divide-x divide-slate-100 dark:divide-slate-800">
                    {/* Top Row */}
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Today</span>
                        <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                      </div>
                      <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(dashboardStats?.todaySales || 0)}</p>
                    </div>
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">This Week</span>
                        <Calendar className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                      <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(dashboardStats?.weekSales || 0)}</p>
                    </div>
                    {/* Bottom Row */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">This Month</span>
                        <CalendarDays className="h-3.5 w-3.5 text-purple-500" />
                      </div>
                      <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(dashboardStats?.monthSales || 0)}</p>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">This Year</span>
                        <CalendarRange className="h-3.5 w-3.5 text-orange-500" />
                      </div>
                      <p className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(dashboardStats?.yearSales || 0)}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── ADD PRODUCT / NEW BILL BUTTONS ── */}
      <div className="px-4 py-4">
        <div className="flex gap-3">
          <Link href="/products" className="flex-1">
            <button
              className="w-full h-12 rounded-2xl border-2 flex items-center justify-center gap-2 font-bold text-sm transition-all active:scale-[0.98]"
              style={{
                borderColor: "rgba(139,26,26,0.20)",
                color: "#8B1A1A",
                backgroundColor: "rgba(139,26,26,0.03)",
              }}
              data-testid="button-quick-products"
            >
              <Plus className="h-4 w-4" style={{ color: "#8B1A1A" }} />
              Add Product
            </button>
          </Link>
          <Link href="/products?action=scan" className="flex-1">
            <button
              className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 font-bold text-sm text-white shadow-lg shadow-red-900/20 transition-all active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
              data-testid="button-quick-bill"
            >
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-extrabold">
                ₹
              </span>
              New Bill
              <ArrowRight className="h-4 w-4 opacity-70" />
            </button>
          </Link>
        </div>
      </div>

      {/* ── FAVORITE PRODUCTS (RESPONSIVE GRID) ── */}
      {favoriteProducts.length > 0 && (
        <div
          className={`px-4 pb-4 ${userMode === "pro" && selectedItems.size > 0 ? "mb-40" : ""}`}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
              Favorite Products
            </h2>
            <button
              onClick={() => setHideFavoriteProducts(!hideFavoriteProducts)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider transition-colors"
              style={{
                borderColor: "rgba(139,26,26,0.2)",
                color: "#8B1A1A",
                backgroundColor: "rgba(139,26,26,0.04)",
              }}
            >
              {!hideFavoriteProducts ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
              {!hideFavoriteProducts ? "Hide" : "Show"}
            </button>
          </div>

          <AnimatePresence>
            {!hideFavoriteProducts && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                {/* The Responsive Grid Magic happens here */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
                  {displayedFavorites.map((product) => {
                    const qty = selectedItems.get(product.id)?.quantity || 0;
                    return (
                      <button
                        key={product.id}
                        onClick={() => handleProductSelect(product)}
                        className="relative flex flex-col items-start justify-between p-3 sm:p-4 rounded-2xl min-h-[85px] shadow-sm transition-all active:scale-[0.97]"
                        style={{ backgroundColor: product.color || "#E3F2FD" }}
                        data-testid={`button-favorite-product-${product.id}`}
                      >
                        {qty > 0 && (
                          <span
                            className="absolute top-2 right-2 text-xs font-black rounded-full h-5 w-5 flex items-center justify-center text-white"
                            style={{ backgroundColor: "#8B1A1A" }}
                          >
                            {qty}
                          </span>
                        )}
                        <span className="font-bold text-sm text-slate-800 leading-tight text-left pr-6 truncate w-full">
                          {product.name}
                        </span>
                        <span className="text-base font-extrabold text-slate-900 mt-2">
                          ₹{Number(product.salePrice).toFixed(0)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Normal mode billing summary */}
          {selectedItems.size > 0 && userMode !== "pro" && (
            <div className="mt-4 p-4 border rounded-2xl bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium">Order Total:</span>
                <span className="text-lg font-bold">
                  ₹
                  {(
                    Array.from(selectedItems.values()).reduce(
                      (sum, item) => sum + item.quantity * item.rate,
                      0,
                    ) - billDiscount
                  ).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground mb-4">
                <Zap className="h-3 w-3 text-primary" />
                <span>Reward: +{selectedItems.size > 0 ? "1" : "0"} Point</span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm font-medium whitespace-nowrap">
                  Bill Discount:
                </span>
                <Input
                  type="number"
                  placeholder="₹ 0.00"
                  value={billDiscount || ""}
                  onChange={(e) => setBillDiscount(Number(e.target.value))}
                  className="h-9"
                  data-testid="input-bill-discount"
                />
              </div>
              {userMode === "pro" && (
                <div className="flex items-center justify-between mb-4 px-1">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    GST Bill
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isGstBill}
                    onClick={() => setIsGstBill(!isGstBill)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isGstBill ? "bg-[#8B1A1A]" : "bg-slate-300 dark:bg-slate-600"}`}
                    data-testid="switch-gst-bill"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isGstBill ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={() => handleSell(false)}
                  disabled={isProcessing}
                  size="lg"
                >
                  {isProcessing
                    ? "Processing..."
                    : `Record Sale (₹${(Array.from(selectedItems.values()).reduce((sum, item) => sum + item.quantity * item.rate, 0) - billDiscount).toFixed(2)})`}
                </Button>
                {userMode !== "pro" && (
                  <Button
                    variant="outline"
                    className="w-full border-primary text-primary"
                    onClick={() => handleSell(true)}
                    disabled={isProcessing}
                    size="lg"
                  >
                    Record & Share Invoice
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PROFIT SUMMARY (UNIFIED WIDGET) ── */}
      {(donutData.length > 0 || totalProfit > 0) && (
        <div className="px-4 pb-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white">
                Profit Summary
              </h2>
              <div className="relative">
                <select
                  value={profitPeriod}
                  onChange={(e) =>
                    setProfitPeriod(
                      e.target.value as "weekly" | "monthly" | "yearly",
                    )
                  }
                  className="appearance-none text-[10px] font-black uppercase tracking-widest px-3 py-1.5 pr-7 rounded-full border cursor-pointer focus:outline-none"
                  style={{
                    color: "#8B1A1A",
                    borderColor: "rgba(139,26,26,0.2)",
                    backgroundColor: "rgba(139,26,26,0.05)",
                  }}
                >
                  <option value="weekly">WEEKLY</option>
                  <option value="monthly">MONTHLY</option>
                  <option value="yearly">YEARLY</option>
                </select>
                <ChevronDown
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none"
                  style={{ color: "#8B1A1A" }}
                />
              </div>
            </div>

            {/* Donut chart */}
            <div className="flex flex-col items-center pb-2">
              <div className="relative" style={{ width: 200, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={
                        donutData.length > 0
                          ? donutData
                          : [{ name: "No data", value: 1 }]
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius={62}
                      outerRadius={88}
                      paddingAngle={3}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                    >
                      {(donutData.length > 0
                        ? donutData
                        : [{ name: "No data", value: 1 }]
                      ).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            donutData.length > 0
                              ? DONUT_COLORS[index % DONUT_COLORS.length]
                              : "#E2E8F0"
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
                    PROFIT
                  </p>
                  <p className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                    {formatCurrency(totalProfit)}
                  </p>
                </div>
              </div>

              {donutData.length > 0 && (
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 px-5 pb-4 w-full mt-2">
                  {donutData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor:
                            DONUT_COLORS[index % DONUT_COLORS.length],
                        }}
                      />
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        {entry.name} ({entry.pct}%)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats rows (Grouped List Style) */}
            <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <IndianRupee className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                    Total Profit
                  </span>
                </div>
                <span className="text-sm font-black text-slate-900 dark:text-white">
                  ₹{totalProfit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              </div>
              {topCategory && (
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300 leading-none">
                        Top Category
                      </span>
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-0.5">
                        {topCategory.name}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-black text-slate-900 dark:text-white">
                    ₹{topCategory.value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PRO DISCOUNT DIALOG ── */}
      <Dialog
        open={showProDiscountDialog}
        onOpenChange={setShowProDiscountDialog}
      >
        <DialogContent className="sm:max-w-md rounded-[32px] border-none shadow-2xl bg-[#FFFDF5] dark:bg-slate-900 p-6">
          <DialogTitle className="text-2xl font-extrabold text-slate-900 dark:text-white">
            Apply Discount
          </DialogTitle>
          <p className="text-slate-400 dark:text-slate-500 text-base -mt-2">
            Enter a discount amount for this bill or skip to proceed.
          </p>
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold tracking-widest text-slate-400 uppercase">
                Discount Amount (₹)
              </Label>
              <div className="relative">
                <span className="absolute left-5 inset-y-0 flex items-center text-3xl font-bold text-slate-400 dark:text-slate-500 pointer-events-none">
                  ₹
                </span>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={billDiscount || ""}
                  onChange={(e) => setBillDiscount(Number(e.target.value))}
                  className="h-16 pl-14 pr-4 text-3xl font-extrabold rounded-2xl bg-white dark:bg-slate-800 border-2 border-emerald-400 dark:border-emerald-600 focus:border-emerald-500 text-slate-700 dark:text-white"
                  autoFocus
                  data-testid="input-pro-discount-popup"
                />
              </div>
            </div>
            <div className="flex items-center justify-between px-1 mb-4">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                GST Bill
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isGstBill}
                onClick={() => setIsGstBill(!isGstBill)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isGstBill ? "bg-[#8B1A1A]" : "bg-slate-300 dark:bg-slate-600"}`}
                data-testid="switch-gst-bill-discount-dialog"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isGstBill ? "translate-x-6" : "translate-x-1"}`}
                />
              </button>
            </div>
            <Button
              disabled={isProcessing}
              className="w-full py-5 rounded-2xl text-xl font-bold text-white shadow-lg shadow-red-900/20"
              style={{
                background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
              }}
              onClick={() => handleSell(false, true)}
              data-testid="button-apply-discount-pro"
            >
              {isProcessing ? "Processing..." : billDiscount > 0 ? "Apply & Record" : "Record Without Discount"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RecordSuccessPopup
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        onShare={() => {
          if (lastInvoiceData) {
            const doc = generateInvoice(
              lastInvoiceData.items,
              lastInvoiceData.total,
              lastInvoiceData.discount,
            );
            doc.save(`Invoice_${Date.now()}.pdf`);
            shareOnWhatsApp(
              lastInvoiceData.total,
              lastInvoiceData.items,
              lastInvoiceData.discount,
            );
            setShowSharePopup(false);
          }
        }}
        total={lastInvoiceData?.total || 0}
        discount={lastInvoiceData?.discount || 0}
        items={lastInvoiceData?.items || []}
        summary={lastInvoiceData?.summary || ""}
        orderId={lastInvoiceData?.orderId}
        fullOrderId={lastInvoiceData?.fullOrderId}
        isGstBill={lastInvoiceData?.isGstBill}
      />

      <BillingBar
        selectedItems={Array.from(selectedItems.values())}
        onSell={() => handleSell(false)}
        onBuy={userMode === "pro" ? undefined : () => { }}
        isProcessing={isProcessing}
        discount={billDiscount}
        onSellAndShare={userMode === "pro" ? undefined : () => handleSell(true)}
        userMode={userMode}
        onQuantityChange={handleQuantityChange}
        lastTappedProductId={lastTappedProductId}
      />
    </div>
  );
}